import { useState } from 'react'
import { inject, observer } from 'mobx-react'
import styled from '@emotion/styled'
import useMediaQuery from '@mui/material/useMediaQuery'

import { API_URL } from '../../api'
import { withRouterProps } from '../../utils/wrappers'
import { SVGWrapper } from '../../components'
import { IconExit } from '../../assets/icons'
import { ReactComponent as BackArrow } from '../../assets/icons/IconArrowLeft.svg'
import { ReactComponent as ChatHistoryIcon } from '../../assets/icons/ActionSmallList.svg'
import heyNicBubble from '../../assets/images/heyNicBubbleDefault.png'
import heyNicWordmark from '../../assets/images/heyNicWordmark.png'
import guidedChoiceLogo from '../../assets/images/GuidedChoice-logo.png'
import MobileChatSheet from './MobileChatSheet'
import { CHAT_MOBILE } from './styled'

const Bar = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
  /* Match the regular top nav: its logo is inset by LogoContainer's 20px padding
     (the Box itself has none), which also sets the bar height. Using 20px here
     keeps the shared logo — and thus the whole bar geometry — from shifting. */
  padding: 20px;
  box-shadow: 0 2px 2px 0 rgba(182, 192, 196, 0.15);
  background-color: ${p => p.theme.topNavColor ?? '#ffffff'};

  ${CHAT_MOBILE} {
    height: 64px;
    padding: 0 16px;
  }
`

const LogoButton = styled.button`
  display: flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  ${CHAT_MOBILE} {
    display: none;
  }
`

// Match the regular top nav logo size (TopNav's LogoContainer sizes it 175–220px).
const Logo = styled.img`
  max-width: 220px;
  min-width: 175px;
  width: 100%;
  /* The default theme has no API logo, so use the app's GuidedChoice logo. */
  content: url(${p => (p.$brandLogo ? guidedChoiceLogo : API_URL + p.theme.logo)});
`

// The Hey Nic chat-bubble graphic (same asset as the default top nav).
const Graphic = styled.span`
  display: inline-flex;
  align-items: center;
  & img {
    display: block;
    height: 40px;
    width: auto;
  }
`

// Design: the graphic is right-aligned, clustered just left of Exit (~60px gap),
// which also lands its right edge at ~the same distance from the nav's right edge
// as in the default top nav — so it doesn't shift horizontally on navigation.
const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 60px;
`

const ExitButton = styled.button`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  background: transparent;
  border: none;
  color: ${p => p.theme.mediumGray ?? '#4a606a'};
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 16px;
  font-weight: 600;
`

const IconButton = styled.button`
  display: none;
  ${CHAT_MOBILE} {
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 10px;
    background: transparent;
    cursor: pointer;
    /* Drives the back arrow (currentColor); the list icon has its own fill. */
    color: ${p => p.theme.primaryColor ?? '#038ab7'};
    & svg {
      display: block;
      width: ${p => (p.big ? '40px' : '30px')};
      height: ${p => (p.big ? '40px' : '30px')};
    }
  }
`

// Centered "HEY NIC" wordmark for the mobile header.
const MobileWordmark = styled.img`
  display: block;
  height: auto;
  width: 30%;
  max-width: 175px;
  min-width: 100px;
`

const HeyNicTopNav = props => {
  // inputRef is only forwarded to the Recents sheet, whose tap handlers focus the
  // chat input to keep the mobile keyboard up across a chat switch.
  const { navigate, location, inputRef } = props
  const { hasPlanAccess } = props.store
  const isMobile = useMediaQuery('(max-width: 800px)')
  const [sheetOpen, setSheetOpen] = useState(false)

  // Return to where the user entered from — not browser-back, which could land
  // on the disclaimer left in history.
  const handleExit = () => navigate(location?.state?.from ?? '/home')

  const graphic = (
    <Graphic>
      <img src={heyNicBubble} alt='Hey Nic AI' />
    </Graphic>
  )

  if (isMobile) {
    return (
      <>
        <Bar>
          <IconButton big type='button' onClick={handleExit} aria-label='Back'>
            <BackArrow />
          </IconButton>
          <MobileWordmark src={heyNicWordmark} alt='Hey Nic' />
          <IconButton type='button' onClick={() => setSheetOpen(true)} aria-label='Chat history'>
            <ChatHistoryIcon />
          </IconButton>
        </Bar>
        <MobileChatSheet open={sheetOpen} onClose={() => setSheetOpen(false)} inputRef={inputRef} />
      </>
    )
  }

  return (
    <Bar>
      <LogoButton type='button' onClick={() => navigate('/home')} aria-label='Go to home'>
        <Logo alt='Logo' $brandLogo={!hasPlanAccess} />
      </LogoButton>
      <RightGroup>
        {graphic}
        <ExitButton type='button' onClick={handleExit}>
          Exit <SVGWrapper svg={IconExit} size='large' cursor='pointer' />
        </ExitButton>
      </RightGroup>
    </Bar>
  )
}

export default withRouterProps(inject('store')(observer(HeyNicTopNav)))
